/**
 * Readers per day, as bars.
 *
 * Bars and not a line: at these volumes a day holds 0, 1 or 2 people, and a line drawn through
 * them saturates between the ceiling and the floor on every step — a sawtooth that says nothing.
 * A line also implies a continuum between days, which distinct daily counts do not have.
 *
 * The previous period is **not** drawn as a second series. Interleaving two sawtooths doubled the
 * noise for a comparison that reads better as one number, which the caller puts in the header.
 *
 * Plain elements rather than SVG: bars need no path maths, they reflow at any width without
 * `preserveAspectRatio` distorting them, and each one can carry its own tooltip.
 *
 * @module Shared/components
 */

import { css } from '@emotion/css';
import type { JSX } from 'react';

const wrapperStyle = css`
  display: flex;
  gap: var(--space-m);
  min-width: 0;
`;

/** The scale, so a bar's height is readable as a number instead of only as a shape. */
const axisStyle = css`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  height: 110px;
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  font-variant-numeric: tabular-nums;
`;

const plotStyle = css`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

/*
  `space-between` with a capped bar width, rather than letting each bar take an equal share of the
  container: a month across a full-width panel gives every bar some 80px, and thirty of those read
  as a wall rather than as a series.
*/
const barsStyle = css`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 2px;
  height: 110px;
  padding-bottom: 1px;
  border-bottom: 1px solid var(--color-border-strong);
`;

const barStyle = css`
  flex: 1 1 0;
  min-width: 3px;
  max-width: 14px;
  border-radius: 2px 2px 0 0;
  background: var(--gl-blue);
`;

/** A day with nobody still gets a sliver, so the day exists on screen instead of vanishing. */
const emptyBarStyle = css`
  flex: 1 1 0;
  min-width: 3px;
  max-width: 14px;
  height: 2px;
  border-radius: 1px;
  background: var(--color-border);
`;

const datesStyle = css`
  display: flex;
  justify-content: space-between;
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

export interface ReachChartProps {
  values: number[];
  /** ISO dates aligned with `values`, used for the end labels and each bar's tooltip. */
  dates?: string[];
  /**
   * A caption under **every** column, for a short fixed scale — an NPS 0–10, a rating 1–5.
   *
   * `dates` prints only the two ends, which is right for a month and wrong for a scale: with
   * eleven buckets the reader has to know which column is the 6.
   */
  labels?: string[];
  /** Height of the plot. The default suits a panel; a card wants less. */
  height?: number;
  /** Accessible summary, since the shape itself conveys the meaning. */
  label: string;
}

/** `2026-09-18` → `18/09`, without going through Date and its timezone. */
function shortDate(iso: string | undefined): string {
  if (!iso) return '';
  const [, month, day] = iso.split('-');
  return month && day ? `${day}/${month}` : iso;
}

const labelsStyle = css`
  display: flex;
  justify-content: space-between;
  gap: 2px;
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  font-variant-numeric: tabular-nums;
`;

const labelStyle = css`
  flex: 1 1 0;
  max-width: 14px;
  text-align: center;
`;

export const ReachChart = ({
  values,
  dates,
  labels,
  height,
  label,
}: ReachChartProps): JSX.Element => {
  const max = Math.max(...values, 1);
  const plotHeight = height === undefined ? undefined : `${height}px`;

  return (
    <div className={wrapperStyle}>
      <div className={axisStyle} aria-hidden='true' style={{ height: plotHeight }}>
        <span>{max}</span>
        <span>0</span>
      </div>

      <div className={plotStyle}>
        <div className={barsStyle} role='img' aria-label={label} style={{ height: plotHeight }}>
          {values.map((value, index) => {
            const day = dates?.[index];
            const caption = labels?.[index];
            const title = day
              ? `${shortDate(day)}: ${value} ${value === 1 ? 'leitor' : 'leitores'}`
              : caption
                ? `${caption}: ${value}`
                : `${value}`;

            if (value === 0) {
              return <div key={caption ?? day ?? index} className={emptyBarStyle} title={title} />;
            }

            return (
              <div
                key={caption ?? day ?? index}
                className={barStyle}
                title={title}
                style={{ height: `${Math.max((value / max) * 100, 4)}%` }}
              />
            );
          })}
        </div>

        {labels && labels.length > 0 ? (
          <div className={labelsStyle}>
            {labels.map(caption => (
              <span key={caption} className={labelStyle}>
                {caption}
              </span>
            ))}
          </div>
        ) : dates && dates.length > 0 ? (
          <div className={datesStyle}>
            <span>{shortDate(dates[0])}</span>
            <span>{shortDate(dates[dates.length - 1])}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
};
